import {
  getDefaultMessage,
  parseError,
  type ErrorKind,
  type ErrorPresentation as ParsedErrorPresentation,
} from '../errors/core';

const GENERIC_KINDS: ErrorKind[] = [
  'server',
  'unknown',
  'validation',
  'network',
  'rate_limit',
  'conflict',
  'circuit_breaker',
];

const GENERIC_MESSAGES = new Set(GENERIC_KINDS.map((kind) => getDefaultMessage(kind)));

export interface ApiErrorPresentation extends ParsedErrorPresentation {
  message: string;
  title: string;
}

export interface ErrorNotificationPayload {
  context?: string;
  message: string;
  metadata?: Record<string, unknown>;
  presentation: ApiErrorPresentation;
  title: string;
}

export interface ErrorLogPayload {
  context?: string;
  error: unknown;
  logMessage: string;
  metadata?: Record<string, unknown>;
  presentation: ApiErrorPresentation;
}

export interface ErrorHandlingRuntime {
  logError?: (payload: ErrorLogPayload) => void;
  notifyError?: (payload: ErrorNotificationPayload) => void;
}

export interface GetApiErrorPresentationOptions {
  fallbackMessage?: string;
  title?: string;
}

export interface HandleApiErrorOptions extends GetApiErrorPresentationOptions {
  context?: string;
  log?: boolean;
  logger?: (payload: ErrorLogPayload) => void;
  logMessage?: string;
  metadata?: Record<string, unknown>;
  notificationMessage?: string;
  notifier?: (payload: ErrorNotificationPayload) => void;
  notify?: boolean;
}

export interface HandleFormErrorOptions extends HandleApiErrorOptions {
  focusFirstField?: boolean;
  rootFieldName?: string;
}

export interface ReactHookFormLike {
  setError: (name: string, error: { message: string; type?: string }) => void;
  setFocus?: (name: string) => void;
}

export interface AntdFormLike {
  setFields: (fields: Array<{ errors: string[]; name: string | string[] }>) => void;
}

export interface GenericFormErrorTarget {
  setErrors?: (errors: Record<string, string>) => void;
  setFieldError?: (name: string, message: string) => void;
  setStatus?: (message: string) => void;
}

export type ErrorHandlerFormTarget =
  | AntdFormLike
  | GenericFormErrorTarget
  | ReactHookFormLike
  | ((fieldErrors: Record<string, string[]>, presentation: ApiErrorPresentation) => void);

let runtime: ErrorHandlingRuntime = {};

function isGenericMessage(message: string): boolean {
  return GENERIC_MESSAGES.has(message) || /request failed with status code/i.test(message);
}

function getDefaultTitle(kind: ErrorKind): string {
  switch (kind) {
    case 'auth':
      return 'Please sign in';
    case 'validation':
      return 'Please review your entries';
    case 'not_found':
      return 'Not found';
    case 'network':
      return 'Connection issue';
    case 'rate_limit':
      return 'Too many requests';
    case 'conflict':
      return 'Action conflict';
    case 'server':
    case 'circuit_breaker':
      return 'Service unavailable';
    default:
      return 'Something went wrong';
  }
}

function toRootFieldName(rootFieldName?: string): string {
  return rootFieldName ?? 'root.server';
}

function applyFieldErrors(
  form: Exclude<ErrorHandlerFormTarget, Function>,
  fieldErrors: Record<string, string[]>,
  presentation: ApiErrorPresentation,
  options?: HandleFormErrorOptions,
): void {
  const fieldEntries = Object.entries(fieldErrors).filter(([, errors]) => errors.length > 0);

  if ('setFields' in form && typeof form.setFields === 'function') {
    form.setFields(
      fieldEntries.map(([name, errors]) => ({
        name,
        errors,
      })),
    );
    return;
  }

  if ('setError' in form && typeof form.setError === 'function') {
    fieldEntries.forEach(([name, errors]) => {
      form.setError(name, { type: presentation.kind, message: errors[0] });
    });
    if (options?.focusFirstField !== false) {
      const firstFieldName = fieldEntries[0]?.[0];
      if (firstFieldName && form.setFocus) {
        form.setFocus(firstFieldName);
      }
    }
    return;
  }

  if ('setFieldError' in form && typeof form.setFieldError === 'function') {
    const setFieldError = form.setFieldError;
    fieldEntries.forEach(([name, errors]) => {
      setFieldError(name, errors[0]);
    });
    return;
  }

  if ('setErrors' in form && typeof form.setErrors === 'function') {
    form.setErrors(
      Object.fromEntries(
        fieldEntries.map(([name, errors]) => [name, errors[0]]),
      ),
    );
  }
}

/** Registers app-specific logging and notification handlers for shared error utilities. */
export function configureErrorHandling(nextRuntime: Partial<ErrorHandlingRuntime>): void {
  runtime = {
    ...runtime,
    ...nextRuntime,
  };
}

/** Resets the shared error runtime to its default no-op state for tests and isolated environments. */
export function resetErrorHandlingRuntime(): void {
  runtime = {};
}

/** Converts unknown API errors into a normalized presentation model for UI and logging. */
export function getApiErrorPresentation(
  error: unknown,
  options?: GetApiErrorPresentationOptions,
): ApiErrorPresentation {
  const parsed = parseError(error, options?.fallbackMessage);
  const fallbackMessage = options?.fallbackMessage;
  const message =
    fallbackMessage && isGenericMessage(parsed.friendlyMessage)
      ? fallbackMessage
      : parsed.friendlyMessage;

  return {
    ...parsed,
    friendlyMessage: message,
    message,
    title: options?.title ?? getDefaultTitle(parsed.kind),
  };
}

/** Logs, optionally notifies, and returns the normalized presentation for an unknown API error. */
export function handleApiError(
  error: unknown,
  options?: HandleApiErrorOptions,
): ApiErrorPresentation {
  const presentation = getApiErrorPresentation(error, options);
  const logPayload: ErrorLogPayload = {
    error,
    presentation,
    context: options?.context,
    logMessage: options?.logMessage ?? presentation.message,
    metadata: options?.metadata,
  };

  if (options?.log !== false) {
    (options?.logger ?? runtime.logError)?.(logPayload);
  }

  if (options?.notify !== false) {
    (options?.notifier ?? runtime.notifyError)?.({
      context: options?.context,
      title: presentation.title,
      message: options?.notificationMessage ?? presentation.message,
      metadata: options?.metadata,
      presentation,
    });
  }

  return presentation;
}

/** Maps API validation errors onto a form target while preserving shared logging behavior. */
export function handleFormError(
  error: unknown,
  form: ErrorHandlerFormTarget,
  options?: HandleFormErrorOptions,
): ApiErrorPresentation {
  const presentation = handleApiError(error, {
    ...options,
    notify: options?.notify ?? false,
  });

  if (presentation.fieldErrors && Object.keys(presentation.fieldErrors).length > 0) {
    if (typeof form === 'function') {
      form(presentation.fieldErrors, presentation);
      return presentation;
    }

    applyFieldErrors(form, presentation.fieldErrors, presentation, options);
    return presentation;
  }

  if (typeof form !== 'function') {
    if ('setError' in form && typeof form.setError === 'function') {
      form.setError(toRootFieldName(options?.rootFieldName), {
        type: presentation.kind,
        message: presentation.message,
      });
    } else if ('setStatus' in form && typeof form.setStatus === 'function') {
      form.setStatus(presentation.message);
    }
  }

  return presentation;
}
